/**
 * 인증(Auth) 관련 API 서비스
 * 
 * API 경로 변경 내역:
 * - 다대구: /api/v1/res/auth/dadaegu/*
 * - 로그인: /api/v1/res/auth/login/*
 * - 로그아웃: /api/v1/res/auth/logout/*
 */

import baseApiClient from '@cnd_fe/ky-client'

// 인증 서버 URL (환경변수)
const authServer = import.meta.env.VITE_AUTH_SERVER_URL || ''

// Auth API 클라이언트
const authClient = baseApiClient.extend({
  prefixUrl: `${authServer}/api/v1/res/auth`
})

/**
 * 일반 로그인 (커스텀 회원 로그인)
 * API: POST /api/v1/res/auth/login/custom-member-login
 * @param {Object} params - 로그인 정보
 * @param {string} params.mbrLgnId - 회원 로그인 ID
 * @param {string} params.pswd - 비밀번호
 * @param {string} params.tenant - 테넌트 ('res-user' | 'res-admin')
 * @returns {Promise<{redirectUrl: string}>}
 */
export const customMemberLogin = async (params) => {
  return await authClient
    .post('login/custom-member-login', {
      json: {
        mbrLgnId: params.mbrLgnId,
        pswd: params.pswd,
        tenant: params.tenant
      }
    })
    .json()
}

/**
 * 로그아웃
 * API: POST /api/v1/res/auth/logout/custom-member-logout?tenant=res-user
 * @param {string} tenant - ('res-user' | 'res-admin')
 * @returns {Promise}
 */
export const customMemberLogout = async (tenant = 'res-user') => {
  return await authClient
    .post(`logout/custom-member-logout?tenant=${tenant}`)
    .json()
}

/**
 * SNS 인증 정보 조회 (Redis) - 카카오, 네이버, 다대구 공통
 * API: POST /api/v1/res/auth/sns-auth-info/verify
 * @param {Object} params - 인증 정보
 * @param {string} params.vKey - 검증 키
 * @param {string} params.stateId - 상태 ID
 * @returns {Promise<{result: boolean, data: Object}>}
 */
export const verifySnsAuthInfo = async (params) => {
  return await authClient
    .post('sns-auth-info/verify', {
      json: {
        vKey: params.vKey,
        stateId: params.stateId
      }
    })
    .json()
}

/**
 * 다대구 로그인 브릿지 URL 생성
 * @param {Object} params
 * @param {string} [params.code_challenge] - PKCE code_challenge
 * @param {string} [params.state] - PKCE state
 * @param {string} [params.tenant] - 테넌트 (기본 res-user)
 * @returns {string} 다대구 로그인 브릿지 URL
 */
export const getDadaeguLoginBridgeUrl = (params = {}) => {
  const { code_challenge, state, tenant = 'res-user' } = params
  const qs = new URLSearchParams()
  if (code_challenge) {
    qs.set('code_challenge', code_challenge)
    qs.set('code_challenge_method', 'S256')
  }
  if (state) qs.set('state', state)
  if (tenant) qs.set('tenant', tenant)
  const query = qs.toString()
  return `${authServer}/api/v1/res/auth/dadaegu/dadaegu-bridge${query ? `?${query}` : ''}`
}

/**
 * 다대구 연동(Verify) 브릿지 URL 생성
 * @param {Object} params - 연동 파라미터
 * @param {string} params.vKey - 검증 키
 * @param {string} params.mbrUnqId - 회원 고유 ID
 * @param {string} params.tenant - 테넌트
 * @returns {string} 다대구 연동 브릿지 URL
 */
export const getDadaeguVerifyBridgeUrl = (params) => {
  const { vKey, mbrUnqId, tenant } = params
  return `${authServer}/api/v1/res/auth/dadaegu/dadaegu-verify-bridge?vKey=${vKey}&mbrUnqId=${mbrUnqId}&tenant=${tenant}`
}

/**
 * 소셜 로그인 OAuth URL 생성
 * @param {Object} params - OAuth 파라미터
 * @param {string} params.provider - 소셜 제공자 ('kakao' | 'naver')
 * @param {string} params.tenant - 테넌트
 * @param {boolean} params.popup - 팝업 여부
 * @param {string} [params.action] - 액션 ('verify' for 연동)
 * @param {string} [params.vKey] - 검증 키 (연동 시)
 * @param {string} [params.mbrUnqId] - 회원 고유 ID (연동 시)
 * @param {boolean} [params.forceLogin] - 강제 로그인 여부
 * @param {string} [params.code_challenge] - PKCE code_challenge
 * @param {string} [params.state] - PKCE state
 * @returns {string} OAuth URL
 */
export const getSocialOAuthUrl = (params) => {
  const {
    provider,
    tenant,
    popup = true,
    action,
    vKey,
    mbrUnqId,
    forceLogin = false,
    code_challenge,
    state
  } = params
  
  let authUrl = `${authServer}/oauth2/authorization/${provider}?tenant=${tenant}&popup=${popup}`
  
  // 연동 모드
  if (action === 'verify') {
    authUrl += `&action=verify&vKey=${vKey}&mbrUnqId=${mbrUnqId}`
  }
  
  // 강제 로그인 파라미터
  if (forceLogin) {
    if (provider === 'kakao') {
      authUrl += '&prompt=login'
    } else if (provider === 'naver') {
      authUrl += '&auth_type=reauthenticate'
    }
  }

  if (code_challenge) {
    authUrl += `&code_challenge=${code_challenge}&code_challenge_method=S256`
  }
  if (state) {
    authUrl += `&state=${state}`
  }
  
  return authUrl
}

/**
 * 인증 서버 URL 반환
 * @returns {string}
 */
export const getAuthServerUrl = () => {
  return authServer
}

const toFiniteSeconds = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value)
  if (typeof value === 'string') {
    const parsed = Number(value.trim())
    if (Number.isFinite(parsed)) return Math.floor(parsed)
  }
  return null
}

const extractRemainingSeconds = (body) => {
  if (!body || typeof body !== 'object') return null
  const root = body
  const data = body.data && typeof body.data === 'object' ? body.data : {}
  const candidates = [
    root.displayRemainingSeconds,
    root.remainingSeconds,
    root.serverRemainingSeconds,
    root.expires_in,
    root.expiresIn,
    data.displayRemainingSeconds,
    data.remainingSeconds,
    data.serverRemainingSeconds,
    data.expires_in,
    data.expiresIn
  ]
  for (const v of candidates) {
    const sec = toFiniteSeconds(v)
    if (sec !== null) return sec
  }
  return null
}

/**
 * Access Token 남은 TTL(초) 조회
 * API: POST /api/v1/res/auth/customMember/redis/user-token-info-check?refreshMode=AUTO
 * @param {Object} params
 * @param {string} params.accessToken - Access Token
 * @param {string} [params.refreshMode] - AUTO | FORCE
 * @returns {Promise<number|null>}
 */
export const getAccessTokenRemainingSeconds = async (params) => {
  const info = await getAccessTokenInfoCheck(params)
  return info?.remainingSeconds ?? null
}

/**
 * Access Token TTL 조회 + 원본 응답 반환
 * API: POST /api/v1/res/auth/customMember/redis/user-token-info-check?refreshMode=AUTO
 * @param {Object} params
 * @param {string} params.accessToken - Access Token
 * @param {string} [params.refreshMode] - AUTO | FORCE
 * @returns {Promise<{remainingSeconds: number|null, raw: any} | null>}
 */
export const getAccessTokenInfoCheck = async (params) => {
  const accessToken = params?.accessToken || ''
  if (!accessToken) return null

  const mode = (params?.refreshMode || 'AUTO').toUpperCase()

  try {
    const raw = await authClient
      .post(`customMember/redis/user-token-info-check?refreshMode=${encodeURIComponent(mode)}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Refresh-Mode': mode
        }
      })
      .json()

    return {
      remainingSeconds: extractRemainingSeconds(raw),
      raw
    }
  } catch (error) {
    if (error?.response) {
      let raw = null
      try {
        raw = await error.response.json()
      } catch {
        raw = null
      }

      return {
        remainingSeconds: extractRemainingSeconds(raw),
        raw,
        status: error.response.status
      }
    }
    throw error
  }
}

